import { useState } from 'react';
import { useMigrateConversion } from 'hooks/billing/useMigrateConversion';
import { useReverseMove } from 'hooks/billing/useReverseMove';
import {
  MOVE_REVERSED_STATE,
  MOVE_SUCCESS_STATE,
} from 'network/httpClients/BillingClient/api.schema';
import { captureBillingError } from 'services/sentry/captureBillingError';
import { reloadPage } from 'utils/url';

interface UsePaidMoveFlowParams {
  /** Move completed silently (off-session charge cleared → fully migrated, paid). */
  onMoveComplete: () => void;
}

/**
 * Drives the paid-cohort Move with its 3DS/SCA reversal recovery, shared by the
 * migration popups.
 *
 * `onMove(planSku)` runs the server-side Move. On a silent success (`refunded`)
 * it fires `onMoveComplete` then hard-reloads the page; if the card needs authentication
 * (`paymentActionRequired`) it sets `paymentActionNeeded` so the caller renders
 * the inline consent step; anything else sets `moveFailed`.
 *
 * `onConfirmReverse()` runs when the user consents: it reverses the Move (cancel
 * legacy sub + refund + stamp self-serve), then hard-reloads the page. The reload
 * re-derives the now free-self-serve state cleanly from the server, so the home
 * banner + the auto-open checkout popup (which carries the decaying coupon) fire
 * from a clean slate rather than an in-session transition.
 */
export const usePaidMoveFlow = ({ onMoveComplete }: UsePaidMoveFlowParams) => {
  const migrate = useMigrateConversion();
  const reverse = useReverseMove();
  const [paymentActionNeeded, setPaymentActionNeeded] = useState(false);
  const [moveFailed, setMoveFailed] = useState(false);

  const onMove = (planSku: string) => {
    setMoveFailed(false);
    setPaymentActionNeeded(false);

    migrate.mutate(planSku, {
      onSuccess: (record) => {
        // Off-session charge needs card authentication (3DS): the Move is parked.
        // Show the consent step instead of treating it as done.
        if (record.paymentActionRequired) {
          setPaymentActionNeeded(true);

          return;
        }

        // Silent success (one-click): the sub swapped on the card on file. Close
        // the popup, then hard-reload so the app re-derives the now-migrated paid
        // state from the server (same clean-slate treatment as the reversal).
        if (record.state === MOVE_SUCCESS_STATE) {
          onMoveComplete();
          reloadPage();

          return;
        }

        setMoveFailed(true);
        captureBillingError(
          new Error(`Move did not complete (state: ${record.state})`),
          'migrate-conversion',
        );
      },
      onError: (error) => {
        setMoveFailed(true);
        captureBillingError(error, 'migrate-conversion');
      },
    });
  };

  const onConfirmReverse = () => {
    setMoveFailed(false);

    reverse.mutate(undefined, {
      onSuccess: (record) => {
        // Legacy sub canceled + refunded, account now free self-serve. Hard-reload
        // so the whole app re-derives the new state from the server (banner +
        // checkout nudge fire from a clean slate).
        if (record.state === MOVE_REVERSED_STATE) {
          reloadPage();

          return;
        }

        setMoveFailed(true);
        captureBillingError(
          new Error(`Move reverse did not complete (state: ${record.state})`),
          'move-reverse',
        );
      },
      onError: (error) => {
        setMoveFailed(true);
        captureBillingError(error, 'move-reverse');
      },
    });
  };

  const reset = () => {
    setPaymentActionNeeded(false);
    setMoveFailed(false);
  };

  return {
    onMove,
    onConfirmReverse,
    paymentActionNeeded,
    moveFailed,
    isBusy: migrate.isPending || reverse.isPending,
    reset,
  };
};
